/** @jsxImportSource @emotion/react */
import { Box, ScrollingCarousel, ScrollingCarouselStep, Text } from '@landtrustinc/design-system';
import React, { useCallback, useMemo } from 'react';

import type { FetchPackageFn, PackageCardData } from '../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../utils/analytics';
import PackageCard from './components/PackageCard';
import { usePackageCardsData } from './hooks/usePackageCardsData';

const DEFAULT_PACKAGE_CARDS_LIMIT = 3;

type PackageCardsProps = {
  packageCards?: PackageCardData[] | null;
  messageId: string;
  /** Max number of cards to show (default: 3, overridable via config.packageCardsLimit) */
  packageCardsLimit?: number;
  onFetchPackage?: FetchPackageFn;
  onAnalyticsEvent?: (event: string, data: Record<string, unknown>) => void;
};

const PackageCards: React.FC<PackageCardsProps> = ({
  packageCards,
  messageId,
  packageCardsLimit = DEFAULT_PACKAGE_CARDS_LIMIT,
  onFetchPackage,
  onAnalyticsEvent,
}) => {
  if (!packageCards || packageCards.length === 0) {
    return null;
  }

  const limitedCards = useMemo(
    () => packageCards.slice(0, packageCardsLimit),
    [packageCards, packageCardsLimit]
  );
  const { data: packageCardsData, isLoading } = usePackageCardsData(limitedCards, onFetchPackage);

  const pushPackageCardClickEvent = useCallback(
    (packageId: string, url: string) => {
      onAnalyticsEvent?.(BUCK_EVENTS.PACKAGE_CARD_CLICKED, {
        [ANALYTICS_KEYS.PACKAGE_ID]: packageId,
        [ANALYTICS_KEYS.PACKAGE_URL]: url,
        [ANALYTICS_KEYS.MESSAGE_ID]: messageId,
      });
    },
    [messageId, onAnalyticsEvent]
  );

  const getItemWidth = (cardCount: number) => {
    if (cardCount === 1) {
      return '100%';
    }

    if (cardCount === 2) {
      return 'calc(50% - var(--spacing-4) / 2)';
    }

    return 'calc(45% - var(--spacing-4))';
  };

  if (!isLoading && packageCardsData.length === 0) {
    return null;
  }

  return (
    <Box mt="var(--spacing-3)" display="flex" flexDirection="column" gap="var(--spacing-4)">
      <Text fontWeight="bold">Your Best Matches</Text>

      <ScrollingCarousel
        id={`package-cards-carousel-${messageId}`}
        showButtons={false}
        showDots={false}
        itemWidth={getItemWidth(packageCardsData.length)}
        gap="var(--spacing-4)"
      >
        {packageCardsData.map((packageCardData, cardIndex) => (
          <ScrollingCarouselStep
            key={packageCardData.packageId || cardIndex}
            parentId={`package-cards-carousel-${messageId}`}
          >
            <PackageCard
              packageCardData={packageCardData}
              isLoading={isLoading}
              onCardClick={pushPackageCardClickEvent}
            />
          </ScrollingCarouselStep>
        ))}
      </ScrollingCarousel>
    </Box>
  );
};

export default PackageCards;
